export type NativePlatform = 'android' | 'ios' | 'web' | string;

export type DevServerOrigin = Readonly<{
  hostname: string;
  origin: string;
  port: string;
  protocol: 'http:' | 'https:';
  source: string;
}>;

export type NativeDevHostMetadata = Readonly<{
  chosenHostname: string | null;
  devServerOrigin: DevServerOrigin | null;
}>;

export type LocalUrlResolution = Readonly<{
  reason: string;
  resolvedUrl: string;
}>;

export const LOCALHOST_PATTERN = /\/\/(127\.0\.0\.1|localhost)(?=[:/]|$)/;
export const LOOPBACK_HOSTS = new Set(['127.0.0.1', 'localhost', '::1']);

const HTTPS_TAILSCALE_PORTS = new Set(['443', '8443', '10000']);
const TAILSCALE_SUPABASE_HTTPS_PORT = '8444';

function explicitPort(url: URL): string {
  if (url.port) {
    return url.port;
  }

  return url.protocol === 'https:' ? '443' : '80';
}

function isTailscaleMagicDns(hostname: string): boolean {
  return hostname.endsWith('.ts.net');
}

function inferProtocol(url: URL, hasExplicitProtocol: boolean): 'http:' | 'https:' {
  if (hasExplicitProtocol) {
    return url.protocol === 'https:' ? 'https:' : 'http:';
  }

  if (isTailscaleMagicDns(url.hostname) && HTTPS_TAILSCALE_PORTS.has(explicitPort(url))) {
    return 'https:';
  }

  return 'http:';
}

export function isLoopbackHostname(hostname: string): boolean {
  return LOOPBACK_HOSTS.has(hostname);
}

export function isLoopbackUrl(url: URL): boolean {
  return isLoopbackHostname(url.hostname);
}

export function devServerOriginFromHostUri(
  hostUri: string | undefined,
  source: string
): DevServerOrigin | null {
  if (!hostUri) {
    return null;
  }

  try {
    const hasExplicitProtocol = /^[a-z][a-z0-9+.-]*:\/\//i.test(hostUri);
    const parsedUrl = new URL(hasExplicitProtocol ? hostUri : `http://${hostUri}`);
    const protocol = inferProtocol(parsedUrl, hasExplicitProtocol);
    const port = explicitPort(parsedUrl);
    const displayPort = protocol === 'https:' && port === '443' ? '' : `:${port}`;

    return {
      hostname: parsedUrl.hostname,
      origin: `${protocol}//${parsedUrl.hostname}${displayPort}`,
      port,
      protocol,
      source,
    };
  } catch {
    return null;
  }
}

function resolveViaHttpsDevOrigin(parsedUrl: URL, devServerOrigin: DevServerOrigin): string | null {
  const localPort = explicitPort(parsedUrl);
  let mappedPort: string | null = null;

  if (localPort === '8081') {
    mappedPort = devServerOrigin.port;
  } else if (localPort === '54321') {
    mappedPort = TAILSCALE_SUPABASE_HTTPS_PORT;
  }

  if (!mappedPort) {
    return null;
  }

  parsedUrl.protocol = devServerOrigin.protocol;
  parsedUrl.hostname = devServerOrigin.hostname;
  parsedUrl.port = mappedPort === '443' ? '' : mappedPort;
  return parsedUrl.toString();
}

export function resolveLocalUrlForPlatform(
  url: string,
  platform: NativePlatform,
  devHostMetadata: NativeDevHostMetadata
): LocalUrlResolution {
  if (platform === 'web') {
    return {
      reason: 'web uses configured/window URL without native host rewrite',
      resolvedUrl: url,
    };
  }

  try {
    const parsedUrl = new URL(url);

    if (devHostMetadata.devServerOrigin && isLoopbackUrl(parsedUrl)) {
      if (devHostMetadata.devServerOrigin.protocol === 'https:') {
        const httpsResolvedUrl = resolveViaHttpsDevOrigin(
          parsedUrl,
          devHostMetadata.devServerOrigin
        );
        if (httpsResolvedUrl) {
          return {
            reason: 'rewrote loopback URL to HTTPS native dev service',
            resolvedUrl: httpsResolvedUrl,
          };
        }
      }

      parsedUrl.hostname = devHostMetadata.devServerOrigin.hostname;
      return {
        reason: 'rewrote loopback URL to native dev host',
        resolvedUrl: parsedUrl.toString(),
      };
    }
  } catch {
    return {
      reason: 'URL parsing failed before native host rewrite',
      resolvedUrl: url,
    };
  }

  if (platform !== 'android') {
    return {
      reason: 'non-Android native platform kept configured URL',
      resolvedUrl: url,
    };
  }

  return {
    reason: 'Android emulator fallback rewrite',
    resolvedUrl: url.replace(LOCALHOST_PATTERN, '//10.0.2.2'),
  };
}
